import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Col } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { adminAPIURL } from '../../../../../../../../Config';
import { useDispatch, useSelector } from 'react-redux';
import { GET_POLYGON_REGION, GET_POLYGON_REGION_EMPTY } from '../../../../../../../../../store/actions';
import { debounce } from 'lodash';
import { MapContainer, TileLayer, Polygon, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Loader } from '../../../../../../../../Common';

const ShowPolygons = () => {
  const dispatch = useDispatch();

  const [defaultOptions, setDefaultOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [serviceableCoords, setServiceableCoords] = useState([]);
  const [nonServiceableCoords, setNonServiceableCoords] = useState([]);

  /* ---------------------------- REDUX STATES ---------------------------- */
  const { getPolygonRegion, getPolygonRegionLoading } = useSelector((state: any) => ({
    getPolygonRegion: state.GetPolygonRegion.data,
    getPolygonRegionLoading: state.GetPolygonRegion.loading,
  }));

  const fetchOptions = async (query = '') => {
    try {
      const res = await axios.get(`${adminAPIURL}/v2/get-card-details/inventory_card`, {
        params: { search: query, page_size: 10, page_index: 1, card_name: 'vendors_inventory_card' }, // same API for both initial & search
        withCredentials: true,
      });

      if (res.data.result?.length > 0 && query === '') {
        dispatch({
          type: GET_POLYGON_REGION,
          payload: {
            data: {
              vendorId: res.data.result?.[0]?.id,
            },
          },
        });
      }

      return res.data.result.map(item => ({
        label: item.firstName,
        value: item.id,
      }));
    } catch (err) {
      console.error('Error fetching options:', err);
      return [];
    }
  };

  useEffect(() => {
    if (getPolygonRegion !== null) {
      if (getPolygonRegion?.data?.serviceableRegions?.length > 0) {
        setServiceableCoords(getPolygonRegion?.data?.serviceableRegions ?? []);
      }

      if (getPolygonRegion?.data?.nonServiceableRegions?.length > 0) {
        setNonServiceableCoords(getPolygonRegion?.data?.nonServiceableRegions ?? []);
      }

      dispatch({
        type: GET_POLYGON_REGION_EMPTY,
      });
    }
  }, [getPolygonRegion]);

  useEffect(() => {
    const loadInitialData = async () => {
      const options = await fetchOptions(''); // empty search
      setDefaultOptions(options);

      if (options.length > 0) {
        setSelectedOption(options[0]); // preselect first
      }
    };

    loadInitialData();
  }, []);

  const loadOptions = useMemo(
    () =>
      debounce((inputValue: string, callback: (opts: any[]) => void) => {
        fetchOptions(inputValue).then(callback);
      }, 500), // 500ms debounce
    [],
  );

  const handleChange = async (option: any) => {
    setSelectedOption(option);
    dispatch({
      type: GET_POLYGON_REGION,
      payload: {
        data: {
          vendorId: option?.value,
        },
      },
    });
  };

  return (
    <Col xl={8} md={12} lg={8} className="mb-2">
      {getPolygonRegionLoading && <Loader zIndex={999} />}
      <Card className="rounded-3 quick-link-configuration-card">
        <CardHeader className="border-bottom-0 d-flex justify-content-between">
          {' '}
          <p className="mb-0 fs-14 fw-semibold">Regions</p>
          <AsyncSelect
            cacheOptions
            defaultOptions={defaultOptions} // initial options
            loadOptions={loadOptions} // search options
            value={selectedOption} // controlled selection
            onChange={handleChange}
            placeholder="Search..."
            styles={{
              container: base => ({
                ...base,
                width: '200px', // ✅ works fine
              }),
            }}
          />
        </CardHeader>
        <CardBody style={{ boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.03) !important', height: '300px', width: '100%' }}>
          <MapContainer center={[28.6, 77.2]} zoom={5} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            />
            {/* Serviceable Area (Green) */}
            {serviceableCoords.length > 0 &&
              serviceableCoords.map((polygon: any, index: number) => (
                <>
                  <Polygon
                    key={`serviceable-${index}`}
                    positions={polygon.coordinates.map((coord: number[]) => [coord[1], coord[0]])} // [lat, lng]
                    pathOptions={{ color: 'green', fillOpacity: 0.4 }}
                  />
                  <FitPolygonBounds coords={serviceableCoords[0].coordinates.map((coord: number[]) => [coord[1], coord[0]])} />
                </>
              ))}

            {/* Non-Serviceable Area (Red) */}
            {nonServiceableCoords.length > 0 &&
              nonServiceableCoords.map((polygon: any, index: number) => (
                <Polygon
                  key={`non-serviceable-${index}`}
                  positions={polygon.coordinates.map((coord: number[]) => [coord[1], coord[0]])} // [lat, lng]
                  pathOptions={{ color: 'red', fillOpacity: 0.4 }}
                />
              ))}
          </MapContainer>
        </CardBody>
      </Card>
    </Col>
  );
};

export default ShowPolygons;

const FitPolygonBounds = ({ coords }: { coords: [number, number][] }) => {
  const map = useMap();

  React.useEffect(() => {
    if (coords && coords.length > 0) {
      map.fitBounds(coords);
    }
  }, [coords, map]);

  return null;
};
